import {
    ActionBar,
    ActionBarSeparator,
    Add,
    Avatar,
    Box,
    Button,
    Table as ComponentsTable,
    EmptyState,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { createColumns } from "./UsersTableColumns";
import { AddIcon } from "components/icons/AddIcon";
import GroupIcon from "components/icons/GroupIcon";
import { viewActionsContext } from "components/layout/ApplicationLayout";
import addIconStyle from "components/licenses/delivery-history/add-license-delivery.scss";
import AssignLicensePoolDialog from "components/licenses/licenses-pool/AssignLicensePoolDialog";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import tableStyle from "components/reports/erasure-reports-table.scss";
import SearchView from "components/search/SearchView";
import Table from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import style from "components/users/users.scss";
import {
    AUTH_LICENSE_POOL_CREATE,
    AUTH_LICENSE_POOL_DELETE,
    AUTH_LICENSE_POOL_EDIT,
    AUTH_LICENSE_POOL_VIEW,
    AUTH_USER_EDIT,
    AUTH_USER_GROUP_CREATE,
    AUTH_USER_GROUP_DELETE,
    AUTH_USER_GROUP_EDIT,
    AUTH_USER_GROUP_VIEW,
} from "domain/authority";
import { INTEGRATION_USER_TYPE, MACHINE_USER_TYPE, STANDARD_USER_TYPE, UserType } from "domain/user";
import { AllUserGroups } from "domain/userGroups";
import { Group, UserTableData } from "domain/users";
import { FLAG_UICOMPS_USERS_TABLES } from "services/feature/FeatureFlagService";
import { LicensePoolList, LicensePoolListResponse, licensePoolService } from "services/licenses/LicensePoolService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { userGroupsService } from "services/user-groups/UserGroupsService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { getInitials } from "utils/commonFunctions";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface TableState {
    usersData: UserTableData[];
    cursor: string;
    scrollPosition?: number;
}

export interface Props {
    allLoaded: boolean;
    scrollPosition?: number;
    count: number;
    onUserDelete: () => void;
    onUserEdit: () => void;
    setUsersFetched: (fetched: boolean) => void;
    search?: string;
    onLicensePoolAssigned?: () => void;
    selectedUser: (value: string) => void;
    showDetailsView: (value: boolean) => void;
}

const connector = connect((state: StoreState) => ({
    userDetail: state.userReducer.user,
}));

function removeDuplicateUsers(previousUserData: UserTableData[], currentUserData: UserTableData[]): UserTableData[] {
    const previousEmailIds = new Set(previousUserData.map(({ email }) => email));
    const finalUserData: UserTableData[] = [
        ...previousUserData,
        ...currentUserData.filter(({ email }) => !previousEmailIds.has(email)),
    ];
    return finalUserData;
}

const UsersTable = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const viewActions = React.useContext(viewActionsContext);
    const [tableState, setTableState] = React.useState<TableState>({
        usersData: [],
        cursor: "",
        scrollPosition: 0,
    });
    const [userGroups, setUserGroups] = React.useState<AllUserGroups>({
        cursor: "",
        groups: [],
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [search, setSearchQuery] = React.useState("");
    const [searchGroup, setSearchGroup] = React.useState("");
    const userUuid = props.userDetail ? props.userDetail.uuid : undefined;
    const theme = useTheme();
    const [assignLicensePoolFormVisible, setAssignLicensePoolFormVisible] = React.useState(false);

    const allUsersReference = React.useRef<UserTableData[]>(tableState.usersData);
    const [deselected, setDeselected] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [licensePoolsList, setLicensePoolsList] = React.useState<LicensePoolList[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [searchLicensePool, setSearchLicensePool] = React.useState("");
    const USER_TYPE_TOOLTIPS_TO_TRANSLATION_MAP = new Map<UserType, string>([
        [STANDARD_USER_TYPE, t("UsersTable.standardUserTooltip")],
        [INTEGRATION_USER_TYPE, t("UsersTable.integrationUserTooltip")],
        [MACHINE_USER_TYPE, t("UsersTable.machineUserTooltip")],
    ]);
    const isUsersTableFeatureEnabled = useFeature(FLAG_UICOMPS_USERS_TABLES) as boolean;

    function handleState() {
        setIsLoading((current) => !current);
    }

    const fetchData = (abortController: AbortController, initialLoading: boolean, reinitialized: boolean) => {
        setLoading(true);
        if (reinitialized) {
            setInitialLoading(initialLoading);
        }

        const fetchUsers = userService.fetchUsers(
            abortController,
            search,
            initialLoading ? "" : tableState.cursor,
            searchGroup,
            searchLicensePool
        );
        const fetchGroups = userGroupAccess ? userGroupsService.fetchAllGroups(abortController) : userGroups;

        Promise.all([fetchUsers, fetchGroups, fetchLicensePoolsList()])
            .then(([usersData, groupsData, licensePools]) => {
                allUsersReference.current = reinitialized
                    ? usersData.userTableData
                    : tableState.usersData.concat(usersData.userTableData);

                setTableState((previousState) => ({
                    scrollPosition: previousState.usersData.length - 1,
                    usersData: removeDuplicateUsers(previousState.usersData, usersData.userTableData),
                    cursor: usersData.cursor,
                }));
                setUserGroups({
                    cursor: groupsData.cursor,
                    groups: groupsData.groups,
                });
                if (licensePools && licensePools.length > 0) {
                    setLicensePoolsList(licensePools);
                }
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("UsersTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
                props.setUsersFetched(true);
            });
    };

    const fetchLicensePoolsList = () => {
        if (!licensePoolsAccess) {
            return Promise.resolve([]);
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        return licensePoolService.fetchLicensePoolsList(abortController).then((data: LicensePoolListResponse) => {
            setLicensePoolsList(data.pools);
            return data.pools;
        });
    };

    const showGroupInitials = (groups: Group[]): React.JSX.Element => {
        const maximumGroupIconsCount = 4;
        return isUsersTableFeatureEnabled ? (
            <Box gap={"Xxxs"} verticalAlignment={"Center"}>
                {groups
                    .filter((group) => group.name != null)
                    .slice(0, maximumGroupIconsCount)
                    .map((group) => {
                        return (
                            <Tooltip key={group.name}>
                                <TooltipTrigger>
                                    <Avatar
                                        backgroundColor={group.backgroundColor}
                                        textColor={group.textColor}
                                        value={getInitials(group.name)}
                                        size={"Lg"}
                                    />
                                </TooltipTrigger>
                                <TooltipContent>{group.name}</TooltipContent>
                            </Tooltip>
                        );
                    })}
                {groups.length > maximumGroupIconsCount &&
                    t("UsersTable.remainingGroupsCount", {
                        remainingGroupsCount: groups.length - maximumGroupIconsCount,
                    })}
            </Box>
        ) : (
            <div className={style.groupIcons}>
                {groups
                    .filter((group) => group.name != null)
                    .slice(0, maximumGroupIconsCount)
                    .map((group) => {
                        return (
                            <div key={group.name}>
                                <TextWithTooltip text={group.name}>
                                    <GroupIcon
                                        backgroundColor={group.backgroundColor}
                                        textColor={group.textColor}
                                        groupInitials={getInitials(group.name)}
                                    />
                                </TextWithTooltip>
                            </div>
                        );
                    })}
                {groups.length > maximumGroupIconsCount && (
                    <div className={style.remainingGroupsCount}>
                        {t("UsersTable.remainingGroupsCount", {
                            remainingGroupsCount: groups.length - maximumGroupIconsCount,
                        })}
                    </div>
                )}
            </div>
        );
    };

    function findSelectedUserUuids(users: UserTableData[]): string[] {
        const foundUuids: string[] = [];
        users.forEach((each) => {
            if (each.checked && each.uuid != userUuid) {
                foundUuids.push(each.uuid);
            }
        });
        return foundUuids;
    }

    const isUuidSelected = (uuid: string): boolean => {
        return typeof allUsersReference.current.find((each) => each.uuid === uuid && each.checked) !== "undefined";
    };

    function isUserTypeStandard(userType: string): boolean {
        return userType == STANDARD_USER_TYPE;
    }

    const updateTableStateBasedOnUuid = (checkedValue: boolean, selectedUuid: string) => {
        setTableState((prevState) => ({
            ...prevState,
            usersData: prevState.usersData.map((each) => {
                if (each.uuid === selectedUuid) {
                    each.checked = checkedValue;
                }
                return each;
            }),
            scrollPosition: 0,
        }));
    };

    const updateUserState = (checkedValue: boolean) => {
        setTableState((prevState) => ({
            ...prevState,
            usersData: prevState.usersData.map((each) => {
                each.checked = checkedValue;

                return each;
            }),
        }));
    };

    function fetchUniqueUsers(): UserTableData[] {
        const emails = new Set();

        return allUsersReference.current.filter((user) => {
            if (emails.has(user.email)) {
                return false;
            } else {
                emails.add(user.email);
                return true;
            }
        });
    }

    const hasAllUsersSelected = (): boolean => {
        const foundUsers: UserTableData[] = [];
        const uniqueUsers: UserTableData[] = fetchUniqueUsers();

        allUsersReference.current.forEach((each) => {
            if (each.checked) {
                foundUsers.push(each);
            }
        });
        return foundUsers.length == uniqueUsers.length;
    };

    const isAnyUsersSelected = (): boolean => {
        return typeof allUsersReference.current.find((each) => each.checked) !== "undefined";
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, uuid: string) => {
        setDeselected(false);
        if (event.target.checked) {
            updateTableStateBasedOnUuid(true, uuid);
        } else {
            updateTableStateBasedOnUuid(false, uuid);
        }
    };

    const isLoggedInUserSelected = () => {
        const foundUsers: UserTableData[] = [];
        const foundUncheckedUsers: UserTableData[] = [];
        let userUuidUnChecked = "";
        allUsersReference.current.forEach((each) => {
            if (each.checked) {
                foundUsers.push(each);
            } else {
                foundUncheckedUsers.push(each);
                userUuidUnChecked = each.uuid;
            }
        });
        return userUuidUnChecked == userUuid && foundUsers.length == 0 && foundUncheckedUsers.length == 0;
    };

    const authorized = userSessionService.userHasAnyAuthority([
        AUTH_LICENSE_POOL_CREATE,
        AUTH_LICENSE_POOL_EDIT,
        AUTH_LICENSE_POOL_VIEW,
        AUTH_LICENSE_POOL_DELETE,
    ]);

    const userGroupAccess =
        tenantHasFeatureLicense("FEATURE_USER_GROUPS") &&
        userSessionService.userHasAnyAuthority([
            AUTH_USER_GROUP_CREATE,
            AUTH_USER_GROUP_DELETE,
            AUTH_USER_GROUP_EDIT,
            AUTH_USER_GROUP_VIEW,
        ]);

    const hasLicensePoolFeature = tenantHasFeatureLicense("FEATURE_LICENSE_POOLS");

    const canAssignLicensePool = userSessionService.userHasAnyAuthority([AUTH_USER_EDIT, AUTH_LICENSE_POOL_VIEW]);

    const licensePoolsAccess = hasLicensePoolFeature && authorized;

    /*TODO 7005 Remove this after the Users table rewrite is done*/
    const selectionInformation = userSessionService.hasFeatureLicense("FEATURE_LICENSE_POOLS") && (
        <>
            <div className={style.centeredAlignedItems}>
                <div data-testid={testIds.common.primaryView.table.selectCountLabel}>
                    {t("UsersTable.information.usersSelected", {
                        total: findSelectedUserUuids(tableState.usersData).length,
                    })}
                </div>
                <div className={style.divider} />
                <button
                    onClick={() => {
                        setTableState((prevState) => ({
                            ...prevState,
                            data: prevState.usersData.map((each) => {
                                if (each.checked) {
                                    each.checked = false;
                                }
                                return each;
                            }),
                        }));
                        setDeselected(true);
                    }}
                    data-testid={testIds.common.primaryView.table.deselectButton}
                >
                    {t("UsersTable.information.deselect")}
                </button>

                <div className={style.divider} />
                <button
                    onClick={() => setAssignLicensePoolFormVisible(true)}
                    data-testid={testIds.workArea.user.assignLicensePoolToUsersButton}
                >
                    <AddIcon color={theme.contentBackgroundColor} linecolor={theme.iconFillColor} />
                    <span className={addIconStyle.addMoreButton}>{t("UsersTable.assignLicensePool")}</span>
                </button>
            </div>
        </>
    );

    const usersTableActionBar = (
        <ActionBar>
            <span data-testid={testIds.common.primaryView.table.selectCountLabel}>
                {t("UsersTable.information.usersSelected", {
                    total: findSelectedUserUuids(tableState.usersData).length,
                })}
            </span>
            <ActionBarSeparator />
            <Button
                variant={"Tertiary"}
                onClick={() => {
                    setTableState((prevState) => ({
                        ...prevState,
                        data: prevState.usersData.map((each) => {
                            if (each.checked) {
                                each.checked = false;
                            }
                            return each;
                        }),
                    }));
                    setDeselected(true);
                }}
                testId={testIds.common.primaryView.table.deselectButton}
            >
                {t("UsersTable.information.deselect")}
            </Button>
            <ActionBarSeparator />
            <Button
                variant={"Tertiary"}
                onClick={() => setAssignLicensePoolFormVisible(true)}
                testId={testIds.workArea.user.assignLicensePoolToUsersButton}
                icon={<Add />}
            >
                {t("UsersTable.assignLicensePool")}
            </Button>
        </ActionBar>
    );

    React.useEffect(() => {
        const abortController = new AbortController();
        setTableState({ usersData: [], cursor: "", scrollPosition: 0 });
        fetchData(abortController, true, true);
        return () => {
            abortController.abort();
        };
    }, [props.count, search, searchGroup, isLoading, searchLicensePool]);

    let dataCount = null;
    if (tableState.usersData.length > 0 && props.allLoaded) {
        dataCount = t("UsersTable.searchResultHint", {
            dataCount: tableState.usersData.length,
        });
    }

    {
        /*TODO 6873 Remove isUsersTableFeatureEnabled from params after Users table rewrite is done*/
    }
    const tableColumns = createColumns(
        isUsersTableFeatureEnabled,
        hasLicensePoolFeature,
        canAssignLicensePool,
        isLoggedInUserSelected(),
        hasAllUsersSelected,
        updateUserState,
        isAnyUsersSelected,
        isUuidSelected,
        deselected,
        handleCheckboxChange,
        userUuid,
        licensePoolsList,
        isUserTypeStandard,
        props.onUserDelete,
        props.onUserEdit,
        props.selectedUser,
        props.showDetailsView,
        USER_TYPE_TOOLTIPS_TO_TRANSLATION_MAP,
        tableContainerRef,
        userGroupAccess,
        licensePoolsAccess,
        showGroupInitials
    );

    return (
        <>
            <div className={tableStyle.gridContentLayout}>
                <div className={style.resultActionBarContainer}>
                    <div className={layoutStyle.recordCount}>{dataCount}</div>
                    {/*TODO 7005 Remove this after Users table rewrite is done*/}
                    {!isUsersTableFeatureEnabled && (
                        <div
                            className={classNames(
                                style.selected,
                                dataCount === null ? tableStyle.gridContentEnd : undefined
                            )}
                        >
                            {canAssignLicensePool && isAnyUsersSelected() && selectionInformation}
                        </div>
                    )}

                    <div className={style.filter}>
                        <span className={style.label}>{t("UsersTable.filters.title")}</span>
                        {hasLicensePoolFeature && authorized && (
                            <div className={formStyle.search}>
                                <span>
                                    <select
                                        className={formStyle.select}
                                        onChange={(e) => {
                                            setSearchLicensePool(e.target.value);
                                        }}
                                    >
                                        <option value="">{t("UsersTable.filters.licensePoolFilterAll")}</option>
                                        {licensePoolsList
                                            .sort((a, b) => a.poolName.localeCompare(b.poolName))
                                            .map((pool) => {
                                                return (
                                                    <option key={pool.poolUuid} value={pool.poolUuid}>
                                                        {pool.poolName}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </span>
                            </div>
                        )}

                        {userGroupAccess && (
                            <div className={formStyle.search}>
                                <span>
                                    <select
                                        className={formStyle.select}
                                        onChange={(e) => setSearchGroup(e.target.value)}
                                    >
                                        <option value="">{t("UsersTable.filters.groupFilterAll")}</option>
                                        {userGroups.groups.map((group) => {
                                            return (
                                                <option key={group.uuid} value={group.uuid}>
                                                    {group.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </span>
                            </div>
                        )}
                        <div className={formStyle.search}>
                            <SearchView setSearch={setSearchQuery} searchInProgress={false} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                {/*TODO 6873 Remove after Users table rewrite is done*/}
                {!isUsersTableFeatureEnabled ? (
                    <Table
                        tableIdentity={RepositoryKey.USER_TABLE}
                        data={tableState.usersData}
                        columns={tableColumns}
                        loaded={!initialLoading && props.allLoaded}
                        failureMessage={requestFailureMessage}
                        tooltips={true}
                        scrollTo={tableState.scrollPosition}
                        emptyMessage={t("UsersTable.emptyStateMessage")}
                        testId={testIds.workArea.user.table.itself}
                    />
                ) : (
                    <div className={layoutStyle.tableInnerWrapper}>
                        <ComponentsTable
                            containerRef={tableContainerRef}
                            tableIdentity={RepositoryKey.USER_TABLE}
                            data={tableState.usersData}
                            columns={tableColumns}
                            loaded={!initialLoading && props.allLoaded}
                            scrollTo={tableState.scrollPosition}
                            emptyStateMessage={
                                <EmptyState text={t("UsersTable.emptyStateMessage")} viewActions={viewActions} />
                            }
                            failureMessage={requestFailureMessage}
                            testIds={{ itself: testIds.workArea.user.table.itself }}
                        />
                    </div>
                )}
                {/*TODO 7005 This is the new ActionBar. Remove the constant controlling the visibility after Users table rewrite is done*/}
                {isUsersTableFeatureEnabled &&
                    canAssignLicensePool &&
                    isAnyUsersSelected() &&
                    userSessionService.hasFeatureLicense("FEATURE_LICENSE_POOLS") &&
                    usersTableActionBar}
            </div>
            {tableState.cursor != null &&
                tableState.usersData.length >= 100 &&
                tableState.usersData.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            const abortController = new AbortController();
                            fetchData(abortController, false, false);
                            usageStatisticsService.sendEvent({
                                label: Label.USER,
                                action: Action.LOAD_MORE,
                                category: Category.USER,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}

            {hasLicensePoolFeature && (
                <AssignLicensePoolDialog
                    userUuids={findSelectedUserUuids(tableState.usersData)}
                    licensePoolsList={licensePoolsList}
                    assignLPViewFormVisible={assignLicensePoolFormVisible}
                    setAssignLPViewFormVisible={() => {
                        setAssignLicensePoolFormVisible(false);
                    }}
                    updateLicensePoolToUsers={handleState}
                />
            )}
        </>
    );
};

export default connector(UsersTable);
